import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { CompanyList, CompanySummary, ContactList, ContactSummary } from '@agency/contracts';
import { Button, Drawer, EmptyState, Skeleton, TextField } from '@agency/ui';
import { ApiClientError, get } from '../../lib/api.ts';
import { fmtDate } from '../../lib/format.ts';
import { QueryError } from '../../lib/query-state.tsx';
import { RecordWorkPanel } from './crm-work.tsx';

type Kind = 'contacts' | 'companies';

export function DirectoryPage({ kind }: { kind: Kind }) {
  const [params, setParams] = useSearchParams();
  const search = params.get('q') ?? '';
  const [draft, setDraft] = useState(search);
  useEffect(() => setDraft(search), [search]);
  useEffect(() => {
    if (draft === search) return;
    const timer = setTimeout(() => setParams((current) => {
      const next = new URLSearchParams(current);
      draft ? next.set('q', draft) : next.delete('q');
      return next;
    }, { replace: true }), 300);
    return () => clearTimeout(timer);
  }, [draft, search, setParams]);

  const query = useInfiniteQuery<ContactList | CompanyList, ApiClientError>({
    queryKey: [kind, search],
    initialPageParam: '',
    queryFn: ({ pageParam }) => {
      const query = new URLSearchParams({ limit: '25' });
      if (search) query.set('q', search);
      if (pageParam) query.set('cursor', String(pageParam));
      return get<ContactList | CompanyList>(`/${kind}?${query}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items: (ContactSummary | CompanySummary)[] = query.data?.pages.flatMap((page) => [...page.items] as (ContactSummary | CompanySummary)[]) ?? [];
  const contacts = kind === 'contacts';
  const title = contacts ? 'Contacts' : 'Companies';
  const selected = params.get('record');
  const detail = useQuery<ContactSummary | CompanySummary, ApiClientError>({ queryKey: [kind, 'detail', selected], queryFn: () => get(`/${kind}/${selected}`), enabled: !!selected });
  const close = () => setParams((current) => { const next = new URLSearchParams(current); next.delete('record'); return next; });

  return (
    <>
      <div className="pagehead"><div><h1>{title}</h1><p>{contacts ? 'People linked to accepted inquiries by email.' : 'Organizations linked to accepted inquiries by their submitted name.'}</p></div></div>
      <div className="toolbar"><div className="toolbar__search"><TextField label={`Search ${title.toLowerCase()}`} type="search" value={draft} onChange={(event) => setDraft(event.target.value)} placeholder={contacts ? 'Name or email' : 'Company name'} /></div></div>
      {query.isLoading ? (
        <div role="status"><span className="ag-visually-hidden">Loading {title.toLowerCase()}…</span><Skeleton width="100%" height={160} /></div>
      ) : query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title={search ? `No ${title.toLowerCase()} match` : `No ${title.toLowerCase()} yet`}>
          {search ? 'Try another search.' : 'Accepted inquiries will appear here after a visitor submits a form.'}
        </EmptyState>
      ) : (
        <>
          <div className="ag-table-wrap">
            <table className="ag-table directory-table">
              <caption className="ag-visually-hidden">{title} from accepted inquiries</caption>
              <thead><tr><th scope="col">{contacts ? 'Contact' : 'Company'}</th><th scope="col">Visible leads</th><th scope="col">Latest inquiry</th></tr></thead>
              <tbody>{items.map((item) => {
                const name = 'email' in item ? item.name ?? item.email : item.name;
                return (
                  <tr key={item.id}>
                    <td data-col="name"><span className="ag-cell-2"><Link to={`/${kind}?record=${item.id}`}>{name}</Link><span>{'email' in item ? item.email : item.domain ?? 'Domain not verified'}</span></span></td>
                    <td data-col="leads" className="ag-num">{item.leadCount}</td>
                    <td data-col="latest">{item.lastLeadAt ? fmtDate(item.lastLeadAt) : '—'}</td>
                  </tr>
                );
              })}</tbody>
            </table>
          </div>
          <div className="pager"><span aria-live="polite">Showing {items.length} {title.toLowerCase()}</span>{query.hasNextPage ? <Button onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>Load more</Button> : null}</div>
        </>
      )}
      <Drawer open={!!selected} onClose={close} title={contacts ? 'Contact' : 'Company'} width={560}>
        {detail.isLoading ? <div role="status"><span className="ag-visually-hidden">Loading record…</span><Skeleton height={120} /></div> : detail.isError ? <QueryError error={detail.error} onRetry={() => void detail.refetch()} /> : detail.data ? <>
          <div className="drawer-id"><h3>{'email' in detail.data ? detail.data.name ?? detail.data.email : detail.data.name}</h3></div>
          <dl className="facts"><dt>Visible leads</dt><dd>{detail.data.leadCount}</dd><dt>Latest inquiry</dt><dd>{detail.data.lastLeadAt ? fmtDate(detail.data.lastLeadAt) : '—'}</dd>{'email' in detail.data ? <><dt>Email</dt><dd>{detail.data.email}</dd></> : <><dt>Domain</dt><dd>{detail.data.domain ?? 'Not verified'}</dd></>}</dl>
          <p><Link to={`/leads?${new URLSearchParams({ q: 'email' in detail.data ? detail.data.email : detail.data.name })}`} onClick={close}>View matching leads</Link></p>
          <RecordWorkPanel target={{ targetType: contacts ? 'CONTACT' : 'COMPANY', targetId: detail.data.id }} />
        </> : null}
      </Drawer>
    </>
  );
}
